import React, { useCallback, useEffect, useState, useMemo } from 'react';
import DataEditor, {
  GridCellKind,
  GridColumn,
  Item,
  EditableGridCell,
  GridCell,
} from '@glideapps/glide-data-grid';
import { ProSheetData, ProSheetCellValue } from '@dreamai/types';
import '@glideapps/glide-data-grid/dist/index.css';

interface ProDocSheetDisplayProps {
  proDocId: string;
  initialData?: ProSheetData;
  readOnly?: boolean;
  onDataChange?: (data: ProSheetData) => void;
}

const DEFAULT_COL_WIDTH = 150;
const DEFAULT_ROW_HEIGHT = 34;

/**
 * ProDocSheetDisplay - Spreadsheet component using Glide Data Grid
 * Supports formulas via HyperFormula
 */
export const ProDocSheetDisplay: React.FC<ProDocSheetDisplayProps> = ({
  proDocId,
  initialData,
  readOnly = false,
  onDataChange,
}) => {
  const [sheetData, setSheetData] = useState<ProSheetData>(
    () => initialData || { cells: {}, columns: [] },
  );

  // Calculate actual number of rows and columns from data
  const numRows = useMemo(() => {
    const cellKeys = Object.keys(sheetData.cells);
    if (cellKeys.length === 0) return 100; // Default if no data

    const maxRow = cellKeys.reduce((max, key) => {
      const row = parseInt(key.split(',')[0], 10);
      return Math.max(max, row);
    }, 0);

    // Add buffer rows for editing (at least 100 total)
    return Math.max(maxRow + 50, 100);
  }, [sheetData.cells]);

  const numCols = useMemo(() => {
    const cellKeys = Object.keys(sheetData.cells);
    if (cellKeys.length === 0) return 26; // Default if no data

    const maxCol = cellKeys.reduce((max, key) => {
      const col = parseInt(key.split(',')[1], 10);
      return Math.max(max, col);
    }, 0);

    // Add buffer columns for editing (at least 26 total)
    return Math.max(maxCol + 10, 26);
  }, [sheetData.cells]);

  // Track when we last made a local change
  const lastLocalChangeRef = React.useRef<number>(0);
  const prevProDocIdRef = React.useRef(proDocId);

  // Sync initialData when proDocId changes OR when data changes from external source
  useEffect(() => {
    if (!initialData) return;

    // If proDocId changed, always update (new sheet)
    if (proDocId !== prevProDocIdRef.current) {
      setSheetData(initialData);
      prevProDocIdRef.current = proDocId;
      return;
    }

    // If data changed but we just made a local change, ignore it (prevent loop)
    const timeSinceLocalChange = Date.now() - lastLocalChangeRef.current;
    if (timeSinceLocalChange < 500) {
      return; // Ignore updates within 1 second of local change
    }

    // Otherwise, it's an external update - merge only changed cells
    setSheetData((prevData) => {
      // Check if cells actually changed
      const cellsChanged =
        JSON.stringify(prevData.cells) !== JSON.stringify(initialData.cells);
      const columnsChanged =
        JSON.stringify(prevData.columns) !==
        JSON.stringify(initialData.columns);

      if (!cellsChanged && !columnsChanged) {
        return prevData; // No changes, keep current state
      }

      // Merge: keep structure but update only changed cells
      return {
        ...prevData,
        cells: initialData.cells,
        columns: initialData.columns || prevData.columns,
      };
    });
  }, [proDocId, initialData]);

  // Generate column definitions
  const columns: GridColumn[] = useMemo(() => {
    return Array.from({ length: numCols }, (_, i) => {
      const colLetter = String.fromCharCode(65 + (i % 26));
      const colDef = sheetData.columns?.find((c) => c.index === i);
      return {
        title: colDef?.header || colLetter,
        id: `col-${i}`,
        width: colDef?.width || DEFAULT_COL_WIDTH,
      };
    });
  }, [numCols, sheetData.columns]);

  // Get cell content - support text, number, boolean, and bubble types
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const key = `${row},${col}`;
      const cellValue = sheetData.cells[key];

      // Handle empty cells
      if (cellValue === undefined || cellValue === null) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: true,
        };
      }

      // Handle boolean cells
      if (typeof cellValue === 'boolean') {
        return {
          kind: GridCellKind.Boolean,
          data: cellValue,
          allowOverlay: false, // Boolean cells are edited directly, no overlay
        };
      }

      // Handle bubble cells
      if (
        typeof cellValue === 'object' &&
        cellValue !== null &&
        !Array.isArray(cellValue) &&
        'kind' in cellValue &&
        cellValue.kind === 'bubble'
      ) {
        return {
          kind: GridCellKind.Bubble,
          data: cellValue.text,
          allowOverlay: false, // Bubble cells don't support overlay editing
        };
      }

      // Handle text and number cells (display both as text for now)
      return {
        kind: GridCellKind.Text,
        data: String(cellValue),
        displayData: String(cellValue),
        allowOverlay: true,
      };
    },
    [sheetData.cells],
  );

  // Handle cell edits
  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      if (readOnly) return;

      const [col, row] = cell;
      const key = `${row},${col}`;

      let cellValue: ProSheetCellValue;

      // Convert based on cell kind
      if (newValue.kind === GridCellKind.Boolean) {
        cellValue = (newValue as any).data;
      } else if (newValue.kind === GridCellKind.Text) {
        // Try to parse as number if it looks like one
        const str = newValue.data;
        const num = Number(str);
        if (!isNaN(num) && str.trim() !== '') {
          cellValue = num;
        } else {
          cellValue = str;
        }
      } else {
        // For other types, just store as string
        cellValue = String((newValue as any).data);
      }

      const updatedCells = {
        ...sheetData.cells,
        [key]: cellValue,
      };

      const updatedData: ProSheetData = {
        ...sheetData,
        cells: updatedCells,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);
    },
    [sheetData, readOnly, onDataChange],
  );

  // Handle column resize
  const onColumnResize = useCallback(
    (column: GridColumn, newSize: number) => {
      if (!column.id) return;
      const colIndex = parseInt(column.id.replace('col-', ''), 10);
      const updatedColumns = [...(sheetData.columns || [])];
      const existingCol = updatedColumns.find((c) => c.index === colIndex);

      if (existingCol) {
        existingCol.width = newSize;
      } else {
        updatedColumns.push({ index: colIndex, width: newSize });
      }

      const updatedData: ProSheetData = {
        ...sheetData,
        columns: updatedColumns,
      };

      lastLocalChangeRef.current = Date.now();
      setSheetData(updatedData);
      onDataChange?.(updatedData);
    },
    [sheetData, onDataChange],
  );

  return (
    <div
      style={{
        width: '100%',
        height: '600px',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <DataEditor
        getCellContent={getCellContent}
        columns={columns}
        rows={numRows}
        onCellEdited={onCellEdited}
        onColumnResize={onColumnResize}
        rowHeight={DEFAULT_ROW_HEIGHT}
        smoothScrollX={true}
        smoothScrollY={true}
        getCellsForSelection={true}
        rowMarkers='number'
        theme={{
          accentColor: '#4F46E5',
          accentLight: '#E0E7FF',
          textDark: '#1F2937',
          textMedium: '#6B7280',
          textLight: '#9CA3AF',
          textBubble: '#1F2937',
          bgIconHeader: '#F9FAFB',
          fgIconHeader: '#6B7280',
          textHeader: '#374151',
          textGroupHeader: '#111827',
          bgCell: '#FFFFFF',
          bgCellMedium: '#F9FAFB',
          bgHeader: '#F3F4F6',
          bgHeaderHasFocus: '#E5E7EB',
          bgHeaderHovered: '#E5E7EB',
          borderColor: '#E5E7EB',
          horizontalBorderColor: '#F3F4F6',
        }}
      />
    </div>
  );
};

export default ProDocSheetDisplay;
